
import * as api from 'app/shared/api';
import { ThunkDispatch } from 'redux-thunk';
import { QueryParamsMap } from 'app/RouteDefinition';
import { pageLoaded } from 'app/pages/DiscountEditor/actions';
import { Action, PageLoadedData } from 'app/pages/DiscountEditor/actionTypes';
import { DiscountDto, ProductFilterDto } from 'app/shared/model/DiscountDto';
import { DiscountType } from 'app/shared/model/DiscountType';
import { AgentSelectionMode } from 'app/shared/model/AgentSelectionMode';
import { refreshApplicableProductOfferingsCommand } from 'app/pages/DiscountEditor/commands';
import type { Product } from 'app/shared/model';

type DiscountAndData = {
    discount: DiscountDto,
    data: PageLoadedData
}

export function loadPage(dispatch: ThunkDispatch<any, any, Action>, qs: QueryParamsMap) : Promise<void> {
    const id = qs.id;
    const discountTypeStr : string = qs.type || ""; 
    const discountType : DiscountType | undefined = (<any>DiscountType)[discountTypeStr];

    return fetchBasicServerData(id, discountType)
        .then(lookupDiscountData)
        .then(x => {
            dispatch(pageLoaded(x.discount, x.data));
            dispatch(refreshApplicableProductOfferingsCommand());
        })
}

function lookupDiscountData(discountAndData: DiscountAndData) : Promise<DiscountAndData> {
    const discount = discountAndData.discount;
    const data = discountAndData.data;

    const isAllAgents = (discount.agentsFilter as any).allAgents !== undefined 
        ? (discount.agentsFilter as any).allAgents 
        : discount.agentsFilter.selectionMode === AgentSelectionMode.AllAgents;
    
    const agents = !isAllAgents && discount.agentsFilter.agents.length > 0
        ? api.getAgentsById(false, ... discount.agentsFilter.agents.map(a => a.agentId))
        : Promise.resolve([]);

    return agents.then((agents) => {
        data.agents = agents;
        return {
            discount,
            data
        }
    });
}

function addFilterCodes(codes: Set<string>, filter?: ProductFilterDto) {
    filter?.codes?.forEach((code) => {
        if (code) codes.add(code);
    });
}

function collectProductCodesFromDiscount(discount: DiscountDto): string[] {
    const codes = new Set<string>();
    addFilterCodes(codes, discount.productFilter);
    discount.actions?.forEach((action) => addFilterCodes(codes, action.productFilter));
    const required = discount.additionalCriteria?.requiredProducts;
    addFilterCodes(codes, required?.courseFilter?.productFilter);
    addFilterCodes(codes, required?.accommodationFilter?.productFilter);
    addFilterCodes(codes, required?.transferFilter?.productFilter);
    addFilterCodes(codes, required?.addOnFilter?.productFilter);
    return [...codes];
}

function fetchBasicServerData(discountId: string|undefined, discountType: DiscountType|undefined) {
    const discountPromise = discountId 
        ? api.getDiscount(discountId)
        : api.getBlankDiscount(discountType || DiscountType.specialOffer);

    const schools = api.getAllSchools();
    const countries = api.getAllCountries();
    const currencies = api.getAllCurrencies(); 
    const centres = api.getAllCentres();
    const bookingSystems = api.getAllBookingSystems(); 
    const courseIntensity = api.getCourseIntensities();

    return discountPromise.then(discount => {
        const divisionId = discount?.divisionFilter?.divisionId;
        const numericDivisionId = typeof divisionId === 'number' && divisionId > 0
            ? divisionId
            : (divisionId ? Math.max(0, Number(divisionId)) : 0);
        const fees = numericDivisionId > 0
            ? api.getAllFeesForDivision(numericDivisionId)
            : Promise.resolve([]);
        const namedProducts = api.getProductNames(collectProductCodesFromDiscount(discount))
            .catch(() => [] as Product[]);

        return Promise.all([Promise.resolve(discount), schools, countries, currencies, fees, centres, bookingSystems, courseIntensity, namedProducts]);
    })
        .then(serverData => {
            const discount = serverData[0];
            const data : PageLoadedData = {
                id: discountId,
                products: serverData[8],
                schools: serverData[1],
                countries: serverData[2],
                currencies: serverData[3], 
                fees: serverData[4],
                centres: serverData[5],
                bookingSystems: serverData[6],
                offerings: [],
                agents: [],
                courseIntensity: serverData[7]
            }

            return {discount, data};
        });
}
